/** Iter 632 — customer wizard · Till + Pay steps: bill breakdown, points toggle, checkout (JSX moved verbatim from EngageSessionPanel.jsx). */
import React from "react";
import { Loader2, Star, CreditCard, CheckCircle2, Receipt, ShieldCheck } from "lucide-react";
import { money } from "@/features/customerWizard/helpers";

export default function BillSection({ w }) {
  const {
    s, amounts, billCorrected, confirmMut, checkoutMut, pendingVerification,
    skipped, verified, pointsOnly, spendablePts, usePoints, setUsePoints,
    riskChallenge, riskCode, setRiskCode, riskConfirmMut,
    quote, rewardCode, setRewardCode,
  } = w;
  if (s.bill_amount == null) {
    return (
      <div data-testid="engage-bill-waiting">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          Waiting for the store to enter your bill…
        </div>
      </div>
    );
  }
  return (
    <div data-testid="engage-bill-breakdown">
      {/* Iter-614 — till corrected the bill: amber notice + re-confirm */}
      {billCorrected != null && !s.bill_confirmed && (
        <div
          className="mb-3 rounded-xl bg-amber-50 border border-amber-300 px-3.5 py-2.5 text-xs font-bold text-amber-800"
          data-testid="engage-bill-corrected-notice"
        >
          💡 The till corrected your bill to {money(billCorrected)} — please confirm the new amount below.
        </div>
      )}
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        <Receipt className="w-4 h-4" /> Your bill
      </div>
      <dl className="mt-2 space-y-1.5 text-sm">
        {(() => {
          // WO 801 §1 — render the founder-approved bill table EXACTLY:
          // Bill · {pct}% store discount · Reward points · Reward code ·
          // Service fee (the ONE fee line) · Total. Values come from the
          // live /quote when available, else session.amounts. Never
          // compute fees client-side.
          const qa = quote?.amounts && typeof quote.amounts === "object" ? quote.amounts : amounts;
          const billVal = money(qa?.display_bill_amount ?? qa?.original_amount ?? s.bill_amount);
          const discountVal = Number(qa?.discount_amount) || 0;
          const pointsDiscount = Number(quote?.points_discount_nzd ?? qa?.points_discount_nzd) || 0;
          const codeDiscount = Number(quote?.reward_code_discount_nzd ?? qa?.reward_code_discount_nzd) || 0;
          const serviceFeeTotal = qa?.service_fee_total != null ? Number(qa.service_fee_total) : null;
          // Old bundles' fallback when the backend predates service_fee_total.
          const legacyFee = (Number(qa?.fee_amount) || 0) + (Number(qa?.customer_service_fee) || 0);
          const serviceFee = serviceFeeTotal != null ? serviceFeeTotal : legacyFee;
          const skipFeeNote = !pointsOnly && Number(qa?.customer_service_fee) > 0;
          const totalVal = money(qa?.charge_total ?? s.charge_total ?? s.bill_amount);
          const youSave = Number(quote?.you_save ?? qa?.you_save) || 0;
          const feeLabel = quote?.fee_label || qa?.fee_label || "Service fee";
          // Iter-621 — points-only is always a flat 5%.
          const pctNum = s.review_state === "points_only" ? 5 : Number(s.discount_percent);
          const pct = pctNum > 0 ? `${pctNum.toFixed(0)}% ` : "";
          const row = (label, value, opts = {}) => (
            <div
              key={label}
              data-testid={opts.testId}
              className={`flex items-center justify-between ${opts.total ? "pt-1.5 border-t border-slate-100 font-black text-slate-900" : "text-slate-600"}`}
            >
              <dt>{label}</dt>
              <dd data-testid={opts.valueTestId} className={opts.discount ? "text-emerald-600 font-semibold" : ""}>{value}</dd>
            </div>
          );
          return (
            <>
              {billVal && row("Bill", billVal)}
              {discountVal > 0
                ? row(`${pct}store discount`, `−${money(discountVal)}`, { discount: true })
                : skipped
                ? row("Discount", "skipped — full price")
                : null}
              {pointsDiscount > 0 &&
                row("Reward points", `−${money(pointsDiscount)}`, { discount: true, testId: "cust-pay-points-row" })}
              {codeDiscount > 0 &&
                row("Reward code", `−${money(codeDiscount)}`, { discount: true })}
              <div data-testid="cust-service-fee-row">
                {row(
                  serviceFee === 0 ? `${feeLabel} (covered by the store)` : feeLabel,
                  serviceFee === 0 ? "$0.00" : `+${money(serviceFee)}`,
                  { valueTestId: "cust-pay-service-fee" },
                )}
                {skipFeeNote && (
                  <p className="text-[11px] text-slate-400">
                    Includes $0.20 that comes back as 20 pts if you post within 48 hours.
                  </p>
                )}
              </div>
              {totalVal && row("Total", totalVal, { total: true })}
              {youSave > 0 && (
                <div
                  data-testid="cust-pay-you-save"
                  className="mt-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-700 text-center"
                >
                  🎉 You save {money(youSave)} ·{" "}
                  <span data-testid="cust-till-you-save">{feeLabel} {money(serviceFee)}</span>
                </div>
              )}
              {quote?.reward_code_error && (
                <p className="text-[11px] text-amber-700" data-testid="cust-reward-code-error">
                  {quote.reward_code_error}
                </p>
              )}
            </>
          );
        })()}
      </dl>

      {/* Iter 680 §2 — the customer never waits for the owner: a post that
          needs approval defers the discount (full price now, points back on
          approval). Note shows until the backend flips status to verified. */}
      {s.review_state === "skipped_deferred" && s.owner_review_pending && s.status !== "verified" && (
        <p
          data-testid="cust-deferred-pay-note"
          className="mt-3 rounded-xl bg-blue-50 border border-blue-200 px-3.5 py-2.5 text-xs text-blue-800 leading-relaxed"
        >
          Setting up your payment… you pay full price now and the discount comes back as reward
          points when the store approves your post (within 48 h).
        </p>
      )}

      {!s.bill_confirmed ? (
        <button
          type="button"
          data-testid="engage-confirm-bill"
          onClick={() => confirmMut.mutate()}
          disabled={confirmMut.isPending}
          style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold py-3 text-sm shadow-card disabled:opacity-60"
        >
          {confirmMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {billCorrected != null
            ? `Confirm new total${money(amounts?.charge_total ?? s.charge_total) ? ` (${money(amounts?.charge_total ?? s.charge_total)})` : ""}`
            : "Confirm bill"}
        </button>
      ) : pendingVerification ? (
        /* Iter-558 — proof submitted, awaiting approval: payment
           stays locked until the post is verified. */
        <div className="mt-4">
          <button
            type="button"
            disabled
            data-testid="engage-pay-waiting-verification"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-200 text-slate-500 font-bold py-3 text-sm cursor-not-allowed"
          >
            ⏳ Waiting for the store to approve your post
          </button>
          {/* Iter 664 — owner approval replaces the automated pre-check;
              unanswered posts auto-approve after 48 hours. */}
          <p className="mt-2 text-xs text-center text-slate-500">
            The store will approve it — automatically within 48 hours if they don&apos;t respond.
            Payment unlocks as soon as it&apos;s approved.
          </p>
        </div>
      ) : (
        <>
          {/* Iter-333/335 — pay with NZ points (OFF by default).
              Iter-442+ — redemption unlocks at 50 pts and spends
              in 50-pt steps (backend enforces either way).
              Iter-621 — points-only spends the purchased/gifted
              wallet instead of the earned one. */}
          {spendablePts > 0 && (() => {
            const pointsValue = spendablePts / 100;
            const locked = spendablePts < 50; // capped_by: "min_balance"
            return (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3">
                <label className={`flex items-start gap-2.5 ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`} data-testid="engage-points-toggle-label">
                  <input
                    type="checkbox"
                    data-testid="engage-points-toggle"
                    checked={usePoints && !locked}
                    disabled={locked}
                    onChange={(e) => setUsePoints(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    <span className="inline-flex items-center gap-1 font-bold text-slate-900">
                      <Star className="w-3.5 h-3.5 text-amber-500" /> Use my {pointsOnly ? "purchased/gifted" : "NZ"} points
                    </span>{" "}
                    {locked
                      ? `— ${spendablePts} pts (unlocks at 50 pts — keep collecting!)`
                      : `— ${spendablePts} pts (up to ${money(pointsValue)}, ${
                          pointsOnly ? "up to the full bill" : "max 50% of the bill"
                        })`}
                  </span>
                </label>
                {/* WO 801 — never compute amounts client-side: the preview
                    reads the live /quote (table + Pay button already match). */}
                {usePoints && !locked && Number(quote?.points_discount_nzd) > 0 && (
                  <div className="mt-2 pl-6 text-xs font-bold text-emerald-700" data-testid="engage-points-preview">
                    New total: {money(quote?.amounts?.charge_total)} ({money(quote.points_discount_nzd)} paid with points)
                  </div>
                )}
              </div>
            );
          })()}
          {/* WO 801 §2 — reward code field; the live quote re-prices the
              bill (debounced) and surfaces reward_code_error if invalid. */}
          <div className="mt-3">
            <input
              value={rewardCode}
              onChange={(e) => setRewardCode(e.target.value)}
              placeholder="Reward code (optional)"
              data-testid="cust-reward-code-input"
              className="w-full rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-slate-400"
            />
          </div>
          <button
            type="button"
            data-testid="engage-pay-btn"
            onClick={() =>
              checkoutMut.mutate({
                ...(usePoints && spendablePts >= 50 ? { redeem_points: spendablePts } : {}),
                ...(rewardCode.trim() ? { reward_code: rewardCode.trim() } : {}),
              })
            }
            disabled={checkoutMut.isPending || !!riskChallenge}
            style={{ backgroundColor: "#059669", color: "#ffffff" }}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold py-3 text-sm shadow-card disabled:opacity-60"
          >
            {checkoutMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {(() => {
              // WO 801 — the Pay button is simply `Pay $<charge_total>`
              // from the latest quote (no "(less points)" suffixes).
              const latest = money(
                (quote?.amounts && quote.amounts.charge_total) ?? amounts?.charge_total ?? s.charge_total ?? s.bill_amount,
              );
              return latest ? `Pay ${latest}` : "Pay now";
            })()}
          </button>
          {/* Iter-637 — suspicious-transaction protection: SMS "are you
              doing this?" code box. Payment stays locked until confirmed.
              channel "none" = no mobile on the account — message only. */}
          {riskChallenge && (
            <div
              className="mt-3 rounded-xl bg-sky-50 border border-sky-300 px-3.5 py-3"
              data-testid="cust-risk-challenge"
            >
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                <p className="text-xs font-bold text-sky-900">
                  {riskChallenge.message ||
                    "Quick security check — we've texted a code to your mobile. Enter it to continue with this payment."}
                  {riskChallenge.masked_phone ? (
                    <span className="block mt-0.5 font-semibold text-sky-700">
                      Sent to {riskChallenge.masked_phone}
                    </span>
                  ) : null}
                </p>
              </div>
              {riskChallenge.channel !== "none" && (
                <div className="mt-2.5 flex items-center gap-2">
                  <input
                    value={riskCode}
                    onChange={(e) => setRiskCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit code"
                    maxLength={6}
                    data-testid="cust-risk-code"
                    className="flex-1 min-w-0 rounded-full border border-sky-300 bg-white px-4 py-2.5 text-sm font-bold tracking-[0.2em] text-center focus:outline-none focus:border-sky-500"
                  />
                  <button
                    type="button"
                    data-testid="cust-risk-confirm"
                    disabled={riskConfirmMut.isPending || riskCode.trim().length < 4}
                    onClick={() => riskConfirmMut.mutate()}
                    className="rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold px-5 py-2.5 disabled:opacity-50 transition-colors"
                  >
                    {riskConfirmMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Iter-558 — verified proof: the reward lands immediately.
              Iter-621 — points-only: payment unlocked without review. */}
          {verified &&
            (pointsOnly ? (
              <div className="mt-2 text-xs font-bold text-amber-700 text-center" data-testid="cust-points-only-ready">
                ⭐ No review needed — 5% off applied. Toggle your points below and pay the rest. A
                small service fee applies to the card payment.
              </div>
            ) : (
              <div className="mt-2 text-xs font-bold text-emerald-700 text-center" data-testid="engage-discount-now-note">
                ✅ Your discount and reward points apply right now
              </div>
            ))}
          {/* Iter-335 — earn nudge (classified only, founder-approved).
              Iter-442+ — partner-store earn is now a FLAT 1 pt per visit. */}
          {(() => {
            const chargeTotal = Number(amounts?.charge_total ?? amounts?.total ?? s.charge_total ?? s.bill_amount) || 0; // Iter-357 trimmed contract
            if (chargeTotal <= 0) return null;
            return (
              <div className="mt-2 flex items-center justify-center gap-1 text-xs font-semibold text-amber-700" data-testid="engage-earn-nudge">
                <Star className="w-3.5 h-3.5" /> You&apos;ll earn a point for this visit
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
